import { DndContext, DragEndEvent } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext } from '@dnd-kit/sortable'
import { useEffect, useRef, useState } from 'react'
import { TableComponent } from '../../../components/table'

import { Button } from '../../../components/button'
import CountCard from '../../../components/cards/CountCard'
import { Status } from '../../../components/cards/statusTag'
import Icon from '../../../components/icon'
import Dropdown from '../../../components/inputs/dropdown'
import { SearchInput } from '../../../components/inputs/searchInput'
import AddProductsToCategoryModal from '../../../components/modal/app_modals/addCategoryToProduct'
import { AddSubCategoryModal } from '../../../components/modal/app_modals/addSubCategoryModal'
import { CountCardContainer } from '../../../components/wrapper/CountCardsContainer'
import {
  deepCopy,
  getPageFromSkip,
  getSkipFromPage,
  moveElement,
} from '../../../helpers'
import { useDebounceSearch } from '../../../hooks/useDeboucedSearch'
import { useModalControl } from '../../../hooks/useModalControl'
import {
  useGetSubCategoriesQuery,
  useReorderSubCategoriesMutation,
} from '../../../redux-toolkits/products/products.slice'
import { SingleSubCategory } from '../../../redux-toolkits/products/products.type'

const SubCategories = ({
  categoryId,
  onSubCategoryClick,
}: {
  categoryId: string
  onSubCategoryClick: (subCat: SingleSubCategory) => void
}) => {
  const [query, setQuery] = useState({
    skip: 0,
    limit: 1000,
  })
  const [search, setSearch] = useState<string>()
  const [subCategories, setSubCategories] = useState<SingleSubCategory[]>([])

  const [selectedSubCategory, setSelectedSubCategory] =
    useState<SingleSubCategory>()

  const { setModalOpen, modalOpen, handleModalOpen, handleModalClose, modal } =
    useModalControl()

  const [reorderSubCategories] = useReorderSubCategoriesMutation()
  const isUserReorder = useRef(false)

  const { data, isFetching } = useGetSubCategoriesQuery(
    {
      skip: query.skip,
      limit: query.limit,
      search: search,
      categories: categoryId,
      isAdmin: true,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e

    if (over && active.id !== over.id) {
      const dataCopy: SingleSubCategory[] = deepCopy(subCategories)
      const dataCopyMod = dataCopy.map((subCat) => {
        return {
          id: `${subCat.name}-${subCat._id}`,
          ...subCat,
        }
      })

      const oldIndex = dataCopyMod.findIndex((item) => item.id === active.id)
      const newIndex = dataCopyMod.findIndex((item) => item.id === over.id)

      isUserReorder.current = true
      setSubCategories(moveElement(dataCopy, oldIndex, newIndex))
    }
  }

  useDebounceSearch({
    searchQuery: subCategories,
    onSearch: async () => {
      if (!isUserReorder.current) return

      isUserReorder.current = false

      await reorderSubCategories({
        categoryId: categoryId,
        payload: {
          subcategoryIds: subCategories.map((subCat) => subCat._id),
        },
      })
    },
    delay: 2000,
  })

  useEffect(() => {
    if (data?.data?.result) {
      const sortedSubCategories = [...data.data.result].sort(
        (a, b) => a.sortOrder - b.sortOrder,
      )

      setSubCategories(sortedSubCategories)
    }
  }, [data])

  return (
    <div className="flex flex-col gap-6">
      <div className="w-full gap-4 flex flex-wrap items-center justify-between">
        <div className="!w-full md:!max-w-[400px]">
          <SearchInput
            searchFunction={(search) => {
              setSearch(search === '' ? undefined : search)
              setQuery({ ...query, skip: 0 })
            }}
            placeholder="Search sub-categories"
          />
        </div>

        <Button
          size="medium"
          label="Add subcategory"
          primary
          onClick={() => {
            handleModalOpen('addSubCategoryModal')
          }}
        />
      </div>

      <CountCardContainer className="!my-0">
        <CountCard
          isCurrency={false}
          text="Total sub-categories count"
          count={data?.data.totalCount ?? 0}
        />
      </CountCardContainer>

      <DndContext
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={
            subCategories.map((subCat) => {
              return {
                id: `${subCat.name}-${subCat._id}`,
                ...subCat,
              }
            }) ?? []
          }
        >
          <div className="w-full overflow-x-scroll">
            <TableComponent
              sortable
              headers={['Name', 'Parent sub-category', 'Status', 'Actions']}
              rows={
                subCategories.length
                  ? subCategories.map((singleSubCategory, idx) => {
                      return {
                        id: `${singleSubCategory.name}-${singleSubCategory._id}`,
                        content: [
                          <button
                            key={`${idx}-name`}
                            className="w-full text-left hover:underline"
                            onClick={(): any => {
                              onSubCategoryClick(singleSubCategory)
                            }}
                          >
                            {singleSubCategory.name}
                          </button>,
                          <span
                            key={`${idx}-parent-subcategory`}
                            className="line-clamp-1 text-ellipsis max-w-[250px]"
                          >
                            {singleSubCategory?.parentSubcategory?.name ??
                              'N/A'}
                          </span>,
                          <Status
                            key={`${idx}-status`}
                            type={
                              singleSubCategory.isActive ? 'success' : 'fail'
                            }
                            text={
                              singleSubCategory.isActive ? 'Active' : 'Inactive'
                            }
                          />,
                          <Dropdown
                            key={`${idx}-conntrols`}
                            menuButton={
                              <Icon
                                id="ellipses"
                                height={18}
                                width={18}
                                className=""
                              />
                            }
                            menuItems={[
                              {
                                name: 'Add Products',
                                value: 'add-products',
                                onClick: () => {
                                  handleModalOpen('AddProductsToCategoryModal')
                                  setSelectedSubCategory(singleSubCategory)
                                },
                              },
                            ]}
                          />,
                        ],
                      }
                    })
                  : []
              }
              name="categories-table"
              loading={isFetching}
              isEmpty={[undefined, 0].includes(subCategories.length)}
              currentPage={getPageFromSkip(query.skip, query.limit)}
              totalDataCount={data?.data.totalCount ?? 0}
              pageLimit={query.limit}
              onPageChange={(number) => {
                setQuery({
                  ...query,
                  skip: getSkipFromPage(number, query.limit),
                })
              }}
            />
          </div>
        </SortableContext>
      </DndContext>

      <AddSubCategoryModal
        open={modalOpen && modal === 'addSubCategoryModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
          setSelectedSubCategory(undefined)
        }}
        onSuccessCallback={() => {
          handleModalClose()
          setSelectedSubCategory(undefined)
        }}
        mode="create"
        categoryId={categoryId}
      />

      <AddProductsToCategoryModal
        open={modalOpen && modal === 'AddProductsToCategoryModal'}
        setOpen={setModalOpen}
        subcategoryId={selectedSubCategory?._id ?? ''}
        categoryId={categoryId}
        title={`Add Products to the '${selectedSubCategory?.name}' Subcategory`}
      />
    </div>
  )
}

export default SubCategories
